import { useState } from 'react'
import type {
  Condition,
  Effect,
  EffectVocabulary,
  SimpleCondition,
} from '@rollcall/contracts'
import { conflictingSetTargets } from '@rollcall/rules'
import { useT } from './i18n-context.js'
import type { TFunction } from 'i18next'

/**
 * Редактор эффектов (D53, шаг 4).
 *
 * Каждая строка — четыре зависимых выбора: что меняем, как, насколько
 * и при каком условии. Ни одно из четырёх не набирается руками, кроме
 * числа: путь величины, набранный с опечаткой, — это эффект, который
 * молча не применится, и заметить это некому.
 *
 * Списки приходят с сервера словарём (`content.vocabulary`), а он
 * считается из движка и загруженного контента. Списка величин в этом
 * файле нет и быть не должно: он разошёлся бы с движком на первом же
 * новом правиле.
 *
 * Чего здесь нет и не будет: перезарядки, легендарных действий,
 * реакций и типов урона. Формат эффектов их не выражает — ни для SRD,
 * ни для своего (D5, D21), — и редактор говорит об этом прямо, а не
 * оставляет человека искать несуществующий пункт в списке.
 */

const NUMERIC_OPS = ['set', 'add', 'add_capped', 'mul', 'clamp'] as const
const CONDITION_OPS = ['eq', 'neq', 'gte', 'lte', 'in', 'has', 'has_not'] as const

/**
 * Ссылка записи на саму себя.
 *
 * «Пока надет» — условие на идентификатор предмета, а у новой записи
 * идентификатора ещё нет: он собирается на сервере из владельца
 * и ключа. Редактор пишет эту строку, сервер подставляет настоящий
 * идентификатор при сохранении (D53).
 */
const SELF = 'u:self'

type NumericOp = (typeof NUMERIC_OPS)[number]

/**
 * Перевод по ключу, если он есть, иначе сам ключ.
 *
 * Ветка словаря может оказаться не строкой, а объектом: `trait.armor`
 * у нас — раздел с `light`, `medium` и `heavy`. i18next в таком случае
 * возвращает жалобу вместо перевода, и она попадала прямо на экран.
 * Поэтому проверяется не «перевод нашёлся», а «пришла строка».
 */
function labelOf(t: TFunction, key: string, fallback: string): string {
  // `returnObjects` здесь не украшение: без него ветка словаря
  // (`trait.armor` — это раздел с light, medium и heavy) возвращается
  // не объектом, а жалобой «returned an object instead of string»,
  // и жалоба уезжает прямо на экран.
  const translated: unknown = t(key, { returnObjects: true })
  if (typeof translated !== 'string') return fallback
  return translated === key ? fallback : translated
}

/** Человеческое имя пути величины: перевод, иначе сам путь. */
export function pathLabel(t: TFunction, path: string): string {
  return labelOf(t, `custom.path.${path}`, path)
}

/**
 * Имя признака. Ключи движка переводятся веткой `trait.*`, но не все:
 * `armor` там раздел, а не строка. Тогда пробуется словарь редактора —
 * в нём это «доспех». Английский ключ на экране остаётся последним
 * средством, а не первым.
 */
function traitLabel(t: TFunction, trait: string): string {
  const direct = labelOf(t, `trait.${trait}`, '')
  if (direct !== '') return direct
  return labelOf(t, `custom.option.${trait}`, trait)
}

/** Значение операции одной строкой: число либо ссылка на величину. */
function valueText(t: TFunction, effect: Effect): string {
  switch (effect.op) {
    case 'set':
    case 'add':
      return typeof effect.value === 'number'
        ? String(effect.value)
        : pathLabel(t, effect.value.ref)
    case 'add_capped':
      return t('custom.effects.cappedText', {
        amount:
          typeof effect.value.amount === 'number'
            ? String(effect.value.amount)
            : pathLabel(t, effect.value.amount.ref),
        max: effect.value.max,
      })
    case 'mul':
      return `×${effect.value}`
    case 'clamp':
      return t('custom.effects.clampText', {
        min: effect.value.min ?? '—',
        max: effect.value.max ?? '—',
      })
    case 'grant':
      return traitLabel(t, effect.value)
  }
}

/**
 * Что получится — словами.
 *
 * Не украшение: числовой эффект читается как «ac set 16» только тем,
 * кто его и написал. Мастер должен увидеть «КД: задать 16» до того,
 * как запись уедет на чужой лист.
 */
export function effectText(t: TFunction, effect: Effect): string {
  const target = pathLabel(t, effect.target)
  const value = valueText(t, effect)
  const main =
    effect.op === 'grant'
      ? t('custom.effects.grantText', { target, value })
      : t('custom.effects.opText', {
          target,
          op: t(`custom.effects.op.${effect.op}`),
          value,
        })
  if (effect.condition === null) return main
  return `${main} (${conditionText(t, effect.condition)})`
}

function simpleText(t: TFunction, condition: SimpleCondition): string {
  const path = pathLabel(t, condition.path)
  const value = Array.isArray(condition.value)
    ? condition.value.join(', ')
    : condition.op === 'has' || condition.op === 'has_not'
      ? traitLabel(t, String(condition.value))
      : String(condition.value)
  return t(`custom.effects.cond.${condition.op}`, { path, value })
}

export function conditionText(t: TFunction, condition: Condition): string {
  if ('all' in condition) {
    return condition.all
      .map((item) => simpleText(t, item))
      .join(t('custom.effects.and'))
  }
  return simpleText(t, condition)
}

function simpleList(condition: Condition | null): SimpleCondition[] {
  if (condition === null) return []
  return 'all' in condition ? [...condition.all] : [condition]
}

/** Обратно в условие: один — простое, два и больше — «И» (D22). */
function toCondition(list: readonly SimpleCondition[]): Condition | null {
  if (list.length === 0) return null
  if (list.length === 1) return list[0] ?? null
  return { all: [...list] }
}

function defaultValue(op: NumericOp): Effect['value'] {
  switch (op) {
    case 'add_capped':
      return { amount: 0, max: 0 }
    case 'clamp':
      return { min: 0 }
    case 'mul':
      return 1
    default:
      return 0
  }
}

/** Смена операции меняет и форму значения: `clamp` с числом не бывает. */
function withOp(effect: Effect, op: NumericOp): Effect {
  return {
    target: effect.target,
    op,
    value: defaultValue(op),
    condition: null,
  } as Effect
}

function ConditionRow({
  condition,
  vocabulary,
  onChange,
  onRemove,
}: {
  readonly condition: SimpleCondition
  readonly vocabulary: EffectVocabulary
  readonly onChange: (next: SimpleCondition) => void
  readonly onRemove: () => void
}) {
  const t = useT()
  const traitOp = condition.op === 'has' || condition.op === 'has_not'

  return (
    <div className="app-effect-condition">
      <select
        aria-label={t('custom.effects.condPath')}
        value={condition.path}
        onChange={(event) => {
          onChange({ ...condition, path: event.target.value } as SimpleCondition)
        }}
      >
        {vocabulary.conditionPaths.map((path) => (
          <option key={path} value={path}>
            {pathLabel(t, path)}
          </option>
        ))}
      </select>
      <select
        aria-label={t('custom.effects.condOp')}
        value={condition.op}
        onChange={(event) => {
          const op = event.target.value as SimpleCondition['op']
          const value =
            op === 'has' || op === 'has_not'
              ? (vocabulary.traits[0] ?? 'poison')
              : op === 'in'
                ? [SELF]
                : op === 'gte' || op === 'lte'
                  ? 0
                  : ''
          onChange({ path: condition.path, op, value } as SimpleCondition)
        }}
      >
        {CONDITION_OPS.map((op) => (
          <option key={op} value={op}>
            {t(`custom.effects.condOpName.${op}`)}
          </option>
        ))}
      </select>
      {condition.op === 'in' ? (
        // Список идентификаторов набирать руками нечего: единственный
        // осмысленный случай — «пока надета эта самая запись».
        <select
          aria-label={t('custom.effects.condValue')}
          value={SELF}
          onChange={() => {
            onChange({ path: condition.path, op: 'in', value: [SELF] })
          }}
        >
          <option value={SELF}>{t('custom.effects.self')}</option>
        </select>
      ) : traitOp ? (
        <select
          aria-label={t('custom.effects.condValue')}
          value={String(condition.value)}
          onChange={(event) => {
            onChange({ ...condition, value: event.target.value } as SimpleCondition)
          }}
        >
          {vocabulary.traits.map((trait) => (
            <option key={trait} value={trait}>
              {traitLabel(t, trait)}
            </option>
          ))}
        </select>
      ) : (
        <input
          aria-label={t('custom.effects.condValue')}
          value={String(condition.value ?? '')}
          onChange={(event) => {
            const raw = event.target.value
            const value =
              condition.op === 'gte' || condition.op === 'lte' ? Number(raw) : raw
            onChange({ ...condition, value } as SimpleCondition)
          }}
        />
      )}
      <button type="button" className="app-button app-button-quiet" onClick={onRemove}>
        {t('custom.effects.condRemove')}
      </button>
    </div>
  )
}

function EffectRow({
  effect,
  vocabulary,
  conflict,
  onChange,
  onRemove,
}: {
  readonly effect: Effect
  readonly vocabulary: EffectVocabulary
  readonly conflict: boolean
  readonly onChange: (next: Effect) => void
  readonly onRemove: () => void
}) {
  const t = useT()
  const conditions = simpleList(effect.condition)
  const numberTargets = vocabulary.targets.filter((item) => item.kind === 'number')
  const traitTargets = vocabulary.targets.filter((item) => item.kind === 'traits')
  const targets = effect.op === 'grant' ? traitTargets : numberTargets

  const setCondition = (next: readonly SimpleCondition[]) => {
    onChange({ ...effect, condition: toCondition(next) } as Effect)
  }

  return (
    <li className="app-effect-row">
      <div className="app-effect-main">
        <select
          aria-label={t('custom.effects.target')}
          value={effect.target}
          onChange={(event) => {
            onChange({ ...effect, target: event.target.value } as Effect)
          }}
        >
          {targets.map((item) => (
            <option key={item.path} value={item.path}>
              {pathLabel(t, item.path)}
            </option>
          ))}
        </select>

        {effect.op === 'grant' ? (
          <select
            aria-label={t('custom.effects.trait')}
            value={effect.value}
            onChange={(event) => {
              onChange({ ...effect, value: event.target.value } as Effect)
            }}
          >
            {vocabulary.traits.map((trait) => (
              <option key={trait} value={trait}>
                {traitLabel(t, trait)}
              </option>
            ))}
          </select>
        ) : (
          <>
            <select
              aria-label={t('custom.effects.opLabel')}
              value={effect.op}
              onChange={(event) => {
                onChange(withOp(effect, event.target.value as NumericOp))
              }}
            >
              {NUMERIC_OPS.map((op) => (
                <option key={op} value={op}>
                  {t(`custom.effects.op.${op}`)}
                </option>
              ))}
            </select>
            <ValueField effect={effect} vocabulary={vocabulary} onChange={onChange} />
          </>
        )}

        <button
          type="button"
          className="app-button app-button-quiet"
          onClick={onRemove}
        >
          {t('custom.effects.remove')}
        </button>
      </div>

      {conflict && (
        <p className="app-problems" role="alert">
          {t('custom.effects.conflict', { target: pathLabel(t, effect.target) })}
        </p>
      )}

      <div className="app-effect-conditions">
        {conditions.map((condition, index) => (
          <ConditionRow
            key={`${condition.path}-${index}`}
            condition={condition}
            vocabulary={vocabulary}
            onChange={(next) => {
              setCondition(conditions.map((item, at) => (at === index ? next : item)))
            }}
            onRemove={() => {
              setCondition(conditions.filter((_, at) => at !== index))
            }}
          />
        ))}
        <button
          type="button"
          className="app-button app-button-quiet"
          onClick={() => {
            setCondition([
              ...conditions,
              {
                path: vocabulary.conditionPaths[0] ?? 'level',
                op: 'gte',
                value: 1,
              },
            ])
          }}
        >
          {t('custom.effects.condAdd')}
        </button>
      </div>

      <p className="app-value-note">{effectText(t, effect)}</p>
    </li>
  )
}

/** Насколько: число либо ссылка на другую величину — выбором, не строкой. */
function ValueField({
  effect,
  vocabulary,
  onChange,
}: {
  readonly effect: Effect
  readonly vocabulary: EffectVocabulary
  readonly onChange: (next: Effect) => void
}) {
  const t = useT()

  if (effect.op === 'mul') {
    return (
      <input
        type="number"
        step="0.5"
        min={0}
        aria-label={t('custom.effects.amount')}
        value={effect.value}
        onChange={(event) => {
          onChange({ ...effect, value: Number(event.target.value) })
        }}
      />
    )
  }

  if (effect.op === 'clamp') {
    return (
      <span className="app-effect-pair">
        <input
          type="number"
          aria-label={t('custom.effects.min')}
          value={effect.value.min ?? ''}
          onChange={(event) => {
            const raw = event.target.value
            const min = raw === '' ? undefined : Number(raw)
            onChange({
              ...effect,
              value:
                min === undefined
                  ? { max: effect.value.max ?? 0 }
                  : { ...effect.value, min },
            })
          }}
        />
        <input
          type="number"
          aria-label={t('custom.effects.max')}
          value={effect.value.max ?? ''}
          onChange={(event) => {
            const raw = event.target.value
            const max = raw === '' ? undefined : Number(raw)
            onChange({
              ...effect,
              value:
                max === undefined
                  ? { min: effect.value.min ?? 0 }
                  : { ...effect.value, max },
            })
          }}
        />
      </span>
    )
  }

  if (effect.op === 'add_capped') {
    const amount = effect.value.amount
    return (
      <span className="app-effect-pair">
        <ScalarField
          value={amount}
          vocabulary={vocabulary}
          onChange={(next) => {
            onChange({ ...effect, value: { ...effect.value, amount: next } })
          }}
        />
        <input
          type="number"
          aria-label={t('custom.effects.cap')}
          value={effect.value.max}
          onChange={(event) => {
            onChange({
              ...effect,
              value: { ...effect.value, max: Number(event.target.value) },
            })
          }}
        />
      </span>
    )
  }

  if (effect.op === 'set' || effect.op === 'add') {
    return (
      <ScalarField
        value={effect.value}
        vocabulary={vocabulary}
        onChange={(next) => {
          onChange({ ...effect, value: next })
        }}
      />
    )
  }

  return null
}

/** Число или ссылка: переключатель плюс поле. Ссылка — только из списка. */
function ScalarField({
  value,
  vocabulary,
  onChange,
}: {
  readonly value: number | { readonly ref: string }
  readonly vocabulary: EffectVocabulary
  readonly onChange: (next: number | { readonly ref: string }) => void
}) {
  const t = useT()
  const isRef = typeof value !== 'number'

  return (
    <span className="app-effect-pair">
      <select
        aria-label={t('custom.effects.kind')}
        value={isRef ? 'ref' : 'number'}
        onChange={(event) => {
          onChange(
            event.target.value === 'ref'
              ? { ref: vocabulary.refs[0]?.path ?? 'level' }
              : 0,
          )
        }}
      >
        <option value="number">{t('custom.effects.kindNumber')}</option>
        <option value="ref">{t('custom.effects.kindRef')}</option>
      </select>
      {isRef ? (
        <select
          aria-label={t('custom.effects.ref')}
          value={value.ref}
          onChange={(event) => {
            onChange({ ref: event.target.value })
          }}
        >
          {vocabulary.refs.map((item) => (
            <option key={item.path} value={item.path}>
              {pathLabel(t, item.path)}
            </option>
          ))}
        </select>
      ) : (
        <input
          type="number"
          aria-label={t('custom.effects.amount')}
          value={value}
          onChange={(event) => {
            onChange(Number(event.target.value))
          }}
        />
      )}
    </span>
  )
}

export function EffectEditor({
  effects,
  vocabulary,
  onChange,
}: {
  readonly effects: readonly Effect[]
  readonly vocabulary: EffectVocabulary
  readonly onChange: (next: readonly Effect[]) => void
}) {
  const t = useT()
  const [limits, setLimits] = useState(false)
  // Спор двух `set` показывается в момент сборки, а не при сохранении
  // и тем более не при открытии листа (D18.3, D53).
  const conflicts = new Set(conflictingSetTargets(effects))

  const numberTargets = vocabulary.targets.filter((item) => item.kind === 'number')
  const traitTargets = vocabulary.targets.filter((item) => item.kind === 'traits')

  return (
    <div className="app-effect-editor">
      <ul className="app-effect-list">
        {effects.map((effect, index) => (
          <EffectRow
            key={`${effect.target}-${index}`}
            effect={effect}
            vocabulary={vocabulary}
            conflict={conflicts.has(effect.target)}
            onChange={(next) => {
              onChange(effects.map((item, at) => (at === index ? next : item)))
            }}
            onRemove={() => {
              onChange(effects.filter((_, at) => at !== index))
            }}
          />
        ))}
      </ul>

      <div className="app-actions">
        <button
          type="button"
          className="app-button app-button-quiet"
          onClick={() => {
            onChange([
              ...effects,
              {
                target: numberTargets[0]?.path ?? 'ac',
                op: 'add',
                value: 1,
                condition: null,
              },
            ])
          }}
        >
          {t('custom.effects.add')}
        </button>
        {/* Признак даётся отдельной кнопкой, а не той же формой:
            владение и сопротивление — не число, и «прибавить 1
            к сопротивлению» бессмысленно (D21.3). */}
        <button
          type="button"
          className="app-button app-button-quiet"
          onClick={() => {
            onChange([
              ...effects,
              {
                target: traitTargets[0]?.path ?? 'proficiencies',
                op: 'grant',
                value: vocabulary.traits[0] ?? 'skill.athletics',
                condition: null,
              },
            ])
          }}
        >
          {t('custom.effects.addTrait')}
        </button>
      </div>

      {effects.length > 0 && (
        <div className="app-effect-summary" data-testid="effect-summary">
          <p className="app-field-label">{t('custom.effects.summary')}</p>
          <ul>
            {effects.map((effect, index) => (
              <li key={`${effect.target}-sum-${index}`}>{effectText(t, effect)}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Прямо сказать, чего формат не выражает, — обязанность
          редактора (D53): иначе человек ищет «реакцию» в списке
          величин и решает, что плохо искал. */}
      <div className="app-section-head">
        <button
          type="button"
          className="app-hint-toggle"
          aria-expanded={limits}
          onClick={() => {
            setLimits((current) => !current)
          }}
        >
          ?
        </button>
        <span className="app-field-hint">{t('custom.effects.limitsTitle')}</span>
      </div>
      {limits && <p className="app-field-hint">{t('custom.effects.limits')}</p>}
    </div>
  )
}
